import React, { useLayoutEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { Link } from "react-router-dom";
import { ReactComponent as StatusIcon } from "../../svg/status.svg";
import { getContrastColors } from "../../UIElements";
import { renderIcon } from "../BotCard/BotCard";
import "./UsedByBots.scss";

function groupBotsByVersion(bots) {
    const groups = {};
    bots.forEach(bot => {
        const group = bot.config?.versionGroup || bot.id;
        if (!groups[group]) groups[group] = [];
        groups[group].push(bot);
    });

    Object.values(groups).forEach(groupBots => {
        groupBots.sort((a, b) => {
            if (a.config?.version === 'DEFAULT') return -1;
            if (b.config?.version === 'DEFAULT') return 1;
            return 0;
        });
    });

    return Object.values(groups);
}

function CompactBotItem({ groupBots }) {
    const primaryBot = groupBots[0];
    const config = primaryBot.config || {};
    const bestStatus = groupBots.some(b => b.status?.toLowerCase() === 'active') ? 'active' : 'draft';

    return (
        <span className={`used-by-compact-item ${bestStatus}`}>
            <StatusIcon />
            {config.name || `Bot #${primaryBot.id}`}
        </span>
    );
}

function CompactUsedByBots({ groups }) {
    const containerRef = useRef(null);
    const measureRef = useRef(null);
    const [visibleCount, setVisibleCount] = useState(groups.length);

    const signature = groups.map(g => g[0].id).join(',');

    useLayoutEffect(() => {
        const container = containerRef.current;
        const measure = measureRef.current;
        if (!container || !measure) return;

        const recompute = () => {
            const itemEls = Array.from(measure.querySelectorAll('[data-bot-item]'));
            const badgeEl = measure.querySelector('[data-bot-overflow]');
            if (itemEls.length === 0) return;

            const available = container.clientWidth;
            const style = window.getComputedStyle(measure);
            const gap = parseFloat(style.columnGap || style.gap) || 0;
            const widths = itemEls.map(el => el.offsetWidth);
            const badgeWidth = badgeEl ? badgeEl.offsetWidth : 0;

            const totalAll = widths.reduce((sum, w) => sum + w, 0) + gap * (widths.length - 1);
            if (totalAll <= available) {
                setVisibleCount(groups.length);
                return;
            }

            let acc = 0;
            let count = 0;
            for (let i = 0; i < widths.length; i++) {
                const candidate = acc + (count > 0 ? gap : 0) + widths[i];
                if (candidate + gap + badgeWidth <= available) {
                    acc = candidate;
                    count++;
                } else {
                    break;
                }
            }
            setVisibleCount(Math.max(1, count));
        };

        recompute();
        const ro = new ResizeObserver(recompute);
        ro.observe(container);
        return () => ro.disconnect();
    }, [signature]); // eslint-disable-line react-hooks/exhaustive-deps

    const overflow = groups.length - visibleCount;

    return (
        <span className="used-by-bots compact" ref={containerRef}>
            <span className="used-by-compact-measure" aria-hidden="true" ref={measureRef}>
                {groups.map((groupBots, i) => (
                    <span data-bot-item key={groupBots[0].id}>
                        {i > 0 && <span className="used-by-compact-separator">,</span>}
                        <CompactBotItem groupBots={groupBots} />
                    </span>
                ))}
                <span className="used-by-compact-overflow" data-bot-overflow>... +{groups.length}</span>
            </span>
            {groups.slice(0, visibleCount).map((groupBots, i) => (
                <React.Fragment key={groupBots[0].id}>
                    {i > 0 && <span className="used-by-compact-separator">,</span>}
                    <CompactBotItem groupBots={groupBots} />
                </React.Fragment>
            ))}
            {overflow > 0 && (
                <span className="used-by-compact-overflow">... +{overflow}</span>
            )}
        </span>
    );
}

export default function UsedByBots({ bots, compact = false }) {
    const intl = useIntl();
    const groups = groupBotsByVersion(bots);

    if (compact) {
        return <CompactUsedByBots groups={groups} />;
    }

    return (
        <div className="used-by-bots full">
            {groups.map(groupBots => {
                const primaryBot = groupBots[0];
                const config = primaryBot.config || {};
                const color = config.color || '#9EA5F8';
                const { textColor, iconBackground } = getContrastColors(color);

                return (
                    <Link
                        key={primaryBot.id}
                        to={`/support-bot/${primaryBot.id}/knowledge`}
                        className="used-by-item"
                    >
                        <div className="used-by-icon" style={{ backgroundColor: color, color: textColor }}>
                            <div className="used-by-icon-inner" style={{ backgroundColor: iconBackground }}>
                                {renderIcon(config.icon)}
                            </div>
                        </div>
                        <div className="used-by-details">
                            <span className="used-by-name">{config.name || `Bot #${primaryBot.id}`}</span>
                            <div className="used-by-versions">
                                {groupBots.map(bot => (
                                    <span key={bot.id} className={`used-by-status ${bot.status?.toLowerCase() || 'active'}`}>
                                        <StatusIcon />
                                        {bot.config?.version === 'DEFAULT'
                                            ? intl.formatMessage({ id: 'default', defaultMessage: 'Default' })
                                            : bot.config?.version || intl.formatMessage({ id: 'default', defaultMessage: 'Default' })
                                        }
                                    </span>
                                ))}
                            </div>
                        </div>
                    </Link>
                );
            })}
        </div>
    );
}
